'use client';

import { useState, useEffect, useCallback, useMemo } from 'react';
import dynamic from 'next/dynamic';
import { useLocation } from '@/app/hooks/use-location';
import { useRestaurants } from '@/app/hooks/use-restaurants';
import { useMapSync } from '@/app/hooks/use-map-sync';
import { useMediaQuery } from '@/app/hooks/use-media-query';
import { useFavorites } from '@/app/providers/favorites-provider';
import { useAvgRatings } from '@/app/hooks/use-reviews';
import { isOpenNow } from '@/app/utils/format-helpers';
import { SearchHeader } from '@/app/components/search-header';
import { FilterChips } from '@/app/components/filter-chips';
import { RestaurantList } from '@/app/components/restaurant-list';
import { Pagination } from '@/app/components/pagination';
import { BottomSheet } from '@/app/components/bottom-sheet';
import type { Restaurant } from '@/app/types/restaurant';

const MapboxMap = dynamic(
  () => import('@/app/components/mapbox-map').then((m) => ({ default: m.MapboxMap })),
  { ssr: false, loading: () => <div className="w-full h-full bg-cream animate-pulse" /> }
);

type HomeClientProps = {
  initialRestaurants: Restaurant[];
};

export function HomeClient({ initialRestaurants }: HomeClientProps) {
  const [search, setSearch] = useState('');
  const [filterDistrict, setFilterDistrict] = useState('');
  const [filterMon, setFilterMon] = useState('');
  const [showOpen, setShowOpen] = useState(false);
  const [showFavorites, setShowFavorites] = useState(false);
  const [radius, setRadius] = useState(3000);

  const location = useLocation();
  const { filtered, paginatedItems, totalPages, currentPage, setCurrentPage, districts, foodTypes, isLoading } =
    useRestaurants({ userLocation: location.userLocation, search, filterDistrict, filterMon, showOpen, radius, isOpenNow, initialData: initialRestaurants });
  const { selectedId, setSelectedId, mapRef, panToRestaurant, fitToMarkers } = useMapSync();
  const { isFavorite } = useFavorites();
  const avgRatings = useAvgRatings();

  // FitBounds when filters change
  useEffect(() => {
    const coords = filtered.filter((r) => r.lat && r.lng).map((r) => ({ lat: r.lat!, lng: r.lng! }));
    fitToMarkers(coords);
  }, [filterMon, filterDistrict, showOpen, filtered, fitToMarkers]);

  // Apply favorites filter
  const displayItems = useMemo(() => {
    if (!showFavorites) return paginatedItems;
    return paginatedItems.filter((r) => isFavorite(r.stt));
  }, [paginatedItems, showFavorites, isFavorite]);

  const handleMarkerClick = useCallback((stt: number, lat: number, lng: number) => {
    setSelectedId(stt);
    panToRestaurant(lat, lng);
  }, [setSelectedId, panToRestaurant]);

  const handleCardClick = useCallback((r: Restaurant) => {
    setSelectedId(r.stt);
    if (r.lat && r.lng) panToRestaurant(r.lat, r.lng);
  }, [setSelectedId, panToRestaurant]);

  const handleClearSelection = useCallback(() => {
    setSelectedId(null);
  }, [setSelectedId]);

  // Build chips with favorites toggle
  const chipsWithFav = useMemo(() => {
    return ['❤️ Yêu thích', ...foodTypes];
  }, [foodTypes]);

  const handleChipSelect = useCallback((val: string) => {
    if (val === '❤️ Yêu thích') {
      setShowFavorites(prev => !prev);
      setFilterMon('');
    } else {
      setShowFavorites(false);
      setFilterMon(val);
    }
  }, []);

  const chipSelected = showFavorites ? '❤️ Yêu thích' : filterMon;
  const isTabletUp = useMediaQuery('(min-width: 768px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');

  return (
    <>
      <SearchHeader
        search={search} setSearch={setSearch}
        locationSearch={location.locationSearch} setLocationSearch={location.setLocationSearch}
        locationSuggestions={location.locationSuggestions}
        showSuggestions={location.showSuggestions} setShowSuggestions={location.setShowSuggestions}
        locationError={location.locationError}
        searchLocation={location.searchLocation}
        locationInputRef={location.locationInputRef}
        userLocation={location.userLocation}
        filterDistrict={filterDistrict} setFilterDistrict={setFilterDistrict}
        showOpen={showOpen} setShowOpen={setShowOpen}
        radius={radius} setRadius={setRadius}
        districts={districts} filteredCount={filtered.length}
      />

      <div className="px-4 py-3 bg-cream border-b border-coral-100">
        <FilterChips foodTypes={chipsWithFav} selected={chipSelected} onSelect={handleChipSelect} />
      </div>

      {isTabletUp ? (
        <div className={`grid h-[calc(100vh-160px)] ${isDesktop ? 'grid-cols-[35%_65%]' : 'grid-cols-[40%_60%]'}`}>
          <div className="overflow-y-auto border-r border-coral-100 bg-cream">
            <RestaurantList
              restaurants={displayItems}
              selectedId={selectedId}
              onCardClick={handleCardClick}
              isLoading={isLoading}
              avgRatings={avgRatings}
            />
            <div className="px-4 pb-4">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          </div>
          <div className="relative h-full">
            <MapboxMap
              restaurants={filtered}
              userLocation={location.userLocation}
              selectedId={selectedId}
              onMarkerClick={handleMarkerClick}
              onClearSelection={handleClearSelection}
              mapRef={mapRef}
            />
          </div>
        </div>
      ) : (
        <div className="flex-1 relative min-h-0">
          <div className="absolute inset-0">
            <MapboxMap
              restaurants={filtered}
              userLocation={location.userLocation}
              selectedId={selectedId}
              onMarkerClick={handleMarkerClick}
              onClearSelection={handleClearSelection}
              mapRef={mapRef}
            />
          </div>
          <BottomSheet>
            <RestaurantList
              restaurants={displayItems}
              selectedId={selectedId}
              onCardClick={handleCardClick}
              isLoading={isLoading}
              avgRatings={avgRatings}
            />
            <div className="px-4 pb-4">
              <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
            </div>
          </BottomSheet>
        </div>
      )}
    </>
  );
}
